/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet';

import { LoadingSection } from '@coursera/cds-core';

import type {
  EntWebsiteBlockCerosWidgetQuery as EntWebsiteBlockExternalEmbedWidgetQuery,
  EntWebsiteCerosWidgetFragment as EntWebsiteExternalEmbedWidgetFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage, noYPadding } from 'bundles/ent-website/contentful/core/BlockLayout';
import { entWebsiteBlockExternalEmbedWidgetQuery } from 'bundles/ent-website/queries/EntWebsiteExternalEmbedWidget';
import { withBlockQuery } from 'bundles/ent-website/utils/withBlockQuery';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteExternalEmbedWidgetFragment> & { noBlockLayout?: boolean };

const EMBED_TYPES = {
  ceros: 'ceros',
  navattic: 'navattic',
  vimeo: 'vimeo',
} as const;
type EMBED_TYPES_TYPE = (typeof EMBED_TYPES)[keyof typeof EMBED_TYPES];

export function ExternalEmbedWidget({ content, assembly, noBlockLayout = false }: Props) {
  const [div, setDiv] = React.useState<HTMLDivElement>();
  const [iframe, setIframe] = React.useState<HTMLIFrameElement>();
  const [embedType, setEmbedType] = React.useState<EMBED_TYPES_TYPE | undefined>();
  const [iframeEnabled, setIFrameEnabled] = React.useState(false);

  // Parse the content.embeddedCode as HTMl and extract the div and iframe elements
  // Since parsing HTML is a relatively expensive operation, we only do it once with useEffect
  React.useEffect(() => {
    if (content.embeddedCode) {
      const template = document.createElement('template');
      template.innerHTML = content.embeddedCode;

      const iframeQueryEl = template.content.querySelector<HTMLIFrameElement>('iframe');
      const iframeSrc = iframeQueryEl?.getAttribute('src');
      if (!iframeQueryEl || !iframeSrc) {
        // Error?
        return;
      }

      let iframeSrcValidator: URL;
      try {
        iframeSrcValidator = new URL(iframeSrc);
      } catch {
        // Invalid URL
        return;
      }
      const iframeHostname = iframeSrcValidator.hostname;

      let derivedEmbedType: EMBED_TYPES_TYPE | undefined;
      // Vimeo was not explicitly supported at release but it's embeds got put into the field.
      // for this reason we have the same render flow for both ceros and vimeo.
      if (iframeHostname.includes('ceros')) {
        derivedEmbedType = EMBED_TYPES.ceros;
      }
      if (iframeHostname.includes('vimeo')) {
        derivedEmbedType = EMBED_TYPES.vimeo;
      }

      if (iframeHostname.includes('navattic')) {
        derivedEmbedType = EMBED_TYPES.navattic;
      }

      setEmbedType(derivedEmbedType);

      // Ceros contract has been ended and we now host this within S3.
      if (derivedEmbedType === EMBED_TYPES.ceros || embedType === EMBED_TYPES.vimeo) {
        // Ceros is hard bound to parent child structure
        const divEl = template.content.firstChild as HTMLDivElement;
        const iframeEl = divEl.firstChild as HTMLIFrameElement;
        setDiv(divEl);
        setIframe(iframeEl);
      }

      if (derivedEmbedType === EMBED_TYPES.navattic) {
        const divEl = template.content.querySelectorAll<HTMLDivElement>('div')[0];
        const iframeEl = template.content.querySelectorAll<HTMLIFrameElement>('iframe')[0];
        setDiv(divEl);
        setIframe(iframeEl);
      }
    }
  }, [content.embeddedCode, embedType]);

  const helmetContent = () => {
    // We generate specific script tags based on the contents of the embed
    // to help reduce the amount of third party payloads.

    if (embedType === EMBED_TYPES.ceros || embedType === EMBED_TYPES.vimeo) {
      // This script is coupled to both ceros and vimeo, do not blindly remove.
      return (
        <Helmet>
          <script
            type="text/javascript"
            // eslint-disable-next-line no-restricted-syntax
            src="https://ceros.coursera.org/scroll-proxy.min.js"
          />
        </Helmet>
      );
    }

    if (embedType === EMBED_TYPES.navattic) {
      return (
        <Helmet>
          <script>
            {`void 0===window.navatticQueue&&(window.navatticQueue=[],window.navattic=new Proxy({},{get:function n(t,u,e){return function(){for(var n=arguments.length,t=Array(n),e=0;e<n;e++)t[e]=arguments[e];return navatticQueue.push({function:u,arguments:t})}}}));`}
          </script>
          <script src="https://js.navattic.com/embed-events.js" />
          <style>
            {`.nv-lazy * {      transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);    }    .nv-lazy {      position: relative;      height: 100%;      width: 100%;      display: flex;      cursor: pointer;      align-items: center;      justify-content: center;      overflow: hidden;      border-radius: 0.75rem;      border-width: 1px;      background-size: cover;      background-repeat: no-repeat;    }    .nv-lazy 
             {      position: relative;      display: flex;      align-items: center;      gap: 0.375rem;      border-radius: 0px;      border: 1px solid rgb(229, 231, 235);      background: #fff;      padding: 0.375rem;      font-size: 0.875rem;      line-height: 1.25rem;      font-weight: 500;      box-shadow:        0 0 #0000,        0 0 #0000,        0 10px 15px -3px rgb(0 0 0 / 0.1),        0 4px 6px -4px rgb(0 0 0 / 0.1);    }    .nv-lazy:hover button {      transform: translate(0px, 0px) rotate(0deg) skewX(0deg) skewY(0deg) scaleX(1.05)        scaleY(1.05);    }    .nv-lazy .nv-lazy-overlay {      position: absolute;      inset: 0px;      background-color: rgb(51 65 85);      opacity: 0.4;    }    .nv-lazy:hover .nv-lazy-overlay {      opacity: 0.35;    }`}
          </style>
        </Helmet>
      );
    }

    return null;
  };

  const contentPanel = () => {
    if (embedType === EMBED_TYPES.navattic) {
      // Extracting variables we want to be customizable
      const DEFAULT_CONTAINER_HEIGHT = '600px';

      const isFullScreen = div?.dataset.fullscreen === 'true';
      const backgroundImage = div?.querySelector<HTMLDivElement>('.nv-lazy')?.style.backgroundImage;
      const containerHeight =
        div?.style?.height?.includes('%') === false ? div?.style?.height : DEFAULT_CONTAINER_HEIGHT;

      const navatticStyles = {
        container: css`
          position: relative;
          border: none;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          width: 100%;
          height: ${containerHeight};
          margin: 0;
          padding: 0;
        `,
        containerFullScreen: css`
          height: 100%;
          position: fixed;
          z-index: 99999;
        `,
        loadingOverlay: css`
          background-image: ${backgroundImage};
        `,
        /* Multiple embeds cause styles to override of previous iframes */
        loadingOverlayDisabled: css`
          display: none !important;
        `,
        /* Flex of some layouts causes this to behave strangely. */
        iframe: css`
          border: none;
          width: 100%;
          height: 100%;
          visibility: hidden;
          position: absolute;
          top: 0;
          left: 0;
        `,
        iframeEnabled: css`
          visibility: visible;
        `,
      };

      const handleOnLoad = () => {
        setIFrameEnabled(true);
      };

      return div && iframe ? (
        <div css={[navatticStyles.container, isFullScreen ? navatticStyles.containerFullScreen : undefined]}>
          <div
            className="nv-lazy"
            css={[
              backgroundImage ? navatticStyles.loadingOverlay : undefined,
              iframeEnabled ? navatticStyles.loadingOverlayDisabled : undefined,
            ]}
          >
            <div className="nv-lazy-overlay" />

            <LoadingSection label="Fetching data..." />
          </div>
          <iframe
            src={iframe.src}
            title={iframe.title}
            id="navattic-embed"
            css={[navatticStyles.iframe, iframeEnabled ? navatticStyles.iframeEnabled : undefined]}
            allow="fullscreen"
            loading="eager"
            onLoad={handleOnLoad}
          />
        </div>
      ) : null;
    }

    // For security reason, we don't use the parsed html directly, but instead
    // create the elements manually with the attributes extracted from the parsed html
    // Note:
    //  - Some attributes have different names in React and HTML DOM
    //    (e.g. allowFullScreen (React) vs allowFullscreen (DOM))
    //    we can't simply use the spread operator to copy all attributes from the parsed html
    //  - We need to call the emotion `css` template function directly to parse the CSS string
    //    (`css` is the function usually used like css`...`)
    //    the CSS string must be retrieved with `getAttribute`
    //    because the `style` attribute is a parsed CSSStyleDeclaration,
    //    and is incompatible with the React `style` prop
    if (embedType === EMBED_TYPES.ceros || embedType === EMBED_TYPES.vimeo) {
      return div && iframe ? (
        <div
          id={div.id}
          css={css(div.getAttribute('style'))}
          data-aspectRatio={div.getAttribute('data-aspectRatio')}
          data-mobile-aspectRatio={div.getAttribute('data-mobile-aspectRatio')}
        >
          <iframe
            allowFullScreen={iframe.allowFullscreen}
            src={iframe.src}
            css={css(iframe.getAttribute('style'))}
            frameBorder={iframe.frameBorder}
            className={iframe.className}
            title={iframe.title}
            scrolling={iframe.scrolling}
          />
        </div>
      ) : null;
    }

    return null;
  };

  return (
    <>
      {helmetContent()}

      {noBlockLayout ? (
        contentPanel()
      ) : (
        <BlockLayout assembly={noImage(noYPadding(assembly))} renderContent={contentPanel} />
      )}
    </>
  );
}

export const BlockExternalEmbedWidget = withBlockQuery<
  EntWebsiteExternalEmbedWidgetFragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockExternalEmbedWidgetQuery
>(entWebsiteBlockExternalEmbedWidgetQuery)(ExternalEmbedWidget);
